import React, { useState, useEffect } from 'react';
import { Users, Mail, Send, RefreshCw, Check, Clock, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import ConfirmModal from '../../components/ConfirmModal';
import { toast } from 'sonner';

export default function ComingSoonSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const limit = 50;

  useEffect(() => { loadSubscribers(); }, [page]);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/coming-soon/signups', { params: { page, limit } });
      setSubscribers(res.data.signups || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleLaunch = () => {
    setConfirmDialog({
      title: 'Send Launch Email',
      message: `Send launch announcement to ALL ${total} subscribers? This cannot be undone.`,
      confirmLabel: 'Send to All',
      confirmColor: 'var(--accent-amber)',
      onConfirm: async () => {
        setSending(true);
        try {
          const res = await api.post('/api/admin/coming-soon/launch');
          toast.success(res.data.message || 'Launch emails sent!');
          setSent(true);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to send launch emails');
        } finally {
          setSending(false);
          setConfirmDialog(null);
        }
      }
    });
  };

  const handleClear = () => {
    setConfirmDialog({
      title: 'Clear All Subscribers',
      message: `Delete ALL ${total} subscribers? This cannot be undone.`,
      confirmLabel: 'Clear All',
      confirmColor: 'var(--error)',
      onConfirm: async () => {
        try {
          const res = await api.delete('/api/admin/coming-soon/signups');
          toast.success(res.data.message || 'All subscribers cleared');
          loadSubscribers();
          setSent(false);
        } catch (err) {
          toast.error('Failed to clear subscribers');
        }
        setConfirmDialog(null);
      }
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page-content" style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 24, fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mail size={26} color="var(--accent-amber)" /> Coming Soon Subscribers
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>{total} {total === 1 ? 'person' : 'people'} waiting for launch</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={loadSubscribers} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={handleLaunch} disabled={sending || sent || total === 0}
            style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: sent ? 'var(--success)' : total === 0 ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #F59E0B, #D97706)', color: sent || total === 0 ? '#fff' : '#0B1121', cursor: sending || sent || total === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, opacity: sending ? 0.6 : 1 }}>
            {sent ? <><Check size={16} /> Launched!</> : sending ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Sending...</> : <><Send size={16} /> Launch Announcement</>}
          </button>
          <button onClick={handleClear} disabled={total === 0}
            style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid var(--error)', background: 'transparent', color: 'var(--error)', cursor: total === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, opacity: total === 0 ? 0.4 : 1 }}>
            <Trash2 size={16} /> Clear All
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div style={{ background: 'linear-gradient(145deg, var(--bg-secondary), var(--bg-tertiary))', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 20 }}>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}><Users size={14} style={{ display: 'inline', marginRight: 6 }} />Total Subscribers</div>
          <div style={{ color: 'var(--text-primary)', fontSize: 28, fontWeight: 800 }}>{total}</div>
        </div>
        <div style={{ background: 'linear-gradient(145deg, var(--bg-secondary), var(--bg-tertiary))', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 20 }}>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}><Clock size={14} style={{ display: 'inline', marginRight: 6 }} />Status</div>
          <div style={{ color: sent ? 'var(--success)' : 'var(--accent-amber)', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            {sent ? <><Check size={18} /> Launched</> : <><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-amber)', animation: 'comingSoonPulse 2s ease-in-out infinite' }} /> Pending</>}
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>IP Address</th>
                <th style={thStyle}>Signed Up</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 40 }}><div className="loader"></div></td></tr>
              ) : subscribers.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><Mail size={32} style={{ marginBottom: 8, opacity: 0.3 }} /><p>No subscribers yet</p></td></tr>
              ) : (
                subscribers.map((sub, i) => (
                  <tr key={sub.id} style={{ borderBottom: i < subscribers.length - 1 ? '1px solid var(--border-primary)' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 20px', color: 'var(--text-primary)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Mail size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} /> {sub.email}
                    </td>
                    <td style={{ padding: '14px 20px', color: 'var(--text-tertiary)', fontSize: 12, fontFamily: 'monospace' }}>{sub.ip_address || ', '}</td>
                    <td style={{ padding: '14px 20px', color: 'var(--text-tertiary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {sub.created_at ? new Date(sub.created_at + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ', '}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 20px', borderTop: '1px solid var(--border-primary)' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>Previous</button>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '6px 12px' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Next</button>
          </div>
        )}
      </div>
      {confirmDialog && (
        <ConfirmModal
          open={!!confirmDialog}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmColor={confirmDialog.confirmColor}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

const thStyle = { padding: '12px 20px', textAlign: 'left', color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
const pageBtnStyle = { padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 };